import React, { useEffect, useState, useMemo } from 'react'
import { Box, Typography, CircularProgress } from '@mui/material'
import Notes from '@mui/icons-material/Notes'
import History from '@mui/icons-material/History'
import MonitorHeart from '@mui/icons-material/MonitorHeart'
import { getMPDataWithParams } from 'src/mentor/services'
import { getDataWithParams } from 'src/consumer/services/profileService'
import { getDataWithoutParams } from 'src/consumer/services/featureServices'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
  isCommonResponseSuccessful,
} from 'src/mentor/helpers/utilityFunctions'

const privateNotesCache = new Map()
const privateNotesInFlightRequests = new Map()
const historyNotesCache = new Map()
const historyNotesInFlightRequests = new Map()

const getPrivateNotesForEndpoint = async (apiEndpoint) => {
  if (!apiEndpoint) return []

  if (privateNotesCache.has(apiEndpoint)) {
    return privateNotesCache.get(apiEndpoint)
  }

  if (privateNotesInFlightRequests.has(apiEndpoint)) {
    return privateNotesInFlightRequests.get(apiEndpoint)
  }

  const requestPromise = getDataWithoutParams(apiEndpoint)
    .then((resp) => {
      if (resp?.status === 200 && isCommonResponseSuccessful(resp?.data?.code)) {
        const notesData =
          resp?.data?.data?.['mentor-private-notes'] || resp?.data?.data?.notes || []
        privateNotesCache.set(apiEndpoint, notesData)
        return notesData
      }
      return []
    })
    .catch(() => [])
    .finally(() => {
      privateNotesInFlightRequests.delete(apiEndpoint)
    })

  privateNotesInFlightRequests.set(apiEndpoint, requestPromise)
  return requestPromise
}

const getHistoryNotesListForPatient = async ({
  currentPortal,
  patientId,
  extractedMentorId,
}) => {
  if (!patientId) return []

  const requestKey = `${currentPortal || 'unknown'}::${patientId}::${extractedMentorId || ''}`
  if (historyNotesCache.has(requestKey)) {
    return historyNotesCache.get(requestKey)
  }
  if (historyNotesInFlightRequests.has(requestKey)) {
    return historyNotesInFlightRequests.get(requestKey)
  }

  const requestPromise = (async () => {
    try {
      let resp
      if (currentPortal === 'clinic-assistant') {
        if (!extractedMentorId) {
          return []
        }
        resp = await getMPDataWithParams(`/api/secure/clinic-assistant/patient-history/of-patient`, {
          patientId,
          mentorId: extractedMentorId,
        })
      } else if (currentPortal === 'support') {
        resp = await getMPDataWithParams(`/api/secure/doctor/patient-history/of-patient`, {
          patientId,
        })
      } else {
        resp = await getMPDataWithParams(`/api/secure/doctor/patient-history/of-patient`, {
          patientId,
        })
      }

      if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
        const respData = getDataMapFromCommonResponse(resp)
        const notesList = Array.isArray(respData?.notes) ? respData.notes : []
        historyNotesCache.set(requestKey, notesList)
        return notesList
      }
      return []
    } catch (error) {
      return []
    } finally {
      historyNotesInFlightRequests.delete(requestKey)
    }
  })()

  historyNotesInFlightRequests.set(requestKey, requestPromise)
  return requestPromise
}

/**
 * Component to fetch and display patient history notes, vitals, and private notes
 * for a specific booked event. Fetches data asynchronously without blocking the page.
 */
export default function EventAdditionalData({ bookedEventId, patientId, clinicId, role = 'doctor', hidePrivateNotes = false, mentorId = null, patientHistoryGetApiSignature = null, mentorPrivateNotes = null, useBatchedVitals = false, batchedVitalsLoading = false, batchedVitalsReady = false, batchedVital = null }) {
  const [historyNotes, setHistoryNotes] = useState(null)
  const [vitals, setVitals] = useState(null)
  const [privateNotes, setPrivateNotes] = useState(null)
  const [loading, setLoading] = useState({ historyNotes: false, vitals: false, privateNotes: false })
  const [error, setError] = useState({ historyNotes: null, vitals: null, privateNotes: null })

  // Detect current portal based on URL path
  const currentPortal = useMemo(() => {
    if (typeof window === 'undefined') return null
    const pathname = window.location.pathname
    
    if (pathname.startsWith('/ca-home/') || pathname.startsWith('/kiosk/') || pathname.startsWith('/ca/')) {
      return 'clinic-assistant'
    }
    if (pathname.startsWith('/support-home/') || pathname.startsWith('/support/')) {
      return 'support'
    }
    if (pathname.startsWith('/mentor-home/') || pathname.startsWith('/mentor/') || pathname.startsWith('/teacher/') || pathname.startsWith('/l/')) {
      return 'mentor'
    }
    // Fallback: if role is doctor or mentor, assume mentor portal
    if (role === 'doctor' || role === 'mentor') {
      return 'mentor'
    }
    return null
  }, [role])

  // Extract mentorId from patientHistoryGetApiSignature if not provided directly
  const extractedMentorId = useMemo(() => {
    if (mentorId) return mentorId
    if (patientHistoryGetApiSignature) {
      const match = patientHistoryGetApiSignature.match(/practitioner-id=([^&]+)/)
      return match ? match[1] : null
    }
    return null
  }, [mentorId, patientHistoryGetApiSignature])

  useEffect(() => {
    if (!Array.isArray(mentorPrivateNotes)) return
    const eventNote = mentorPrivateNotes.find((note) => note?.bookedEventId === bookedEventId) || null
    setPrivateNotes(eventNote)
  }, [mentorPrivateNotes, bookedEventId, patientId])

  // Fetch patient history notes
  useEffect(() => {
    if (!bookedEventId || !patientId) return

    const fetchHistoryNotes = async () => {
      setLoading((prev) => ({ ...prev, historyNotes: true }))
      try {
        const notesList = await getHistoryNotesListForPatient({
          currentPortal,
          patientId,
          extractedMentorId,
        })
        const eventNotes = notesList.find(
          (note) => note?.consultationBookedEventId === bookedEventId,
        )
        setHistoryNotes(eventNotes)
      } catch (err) {
        console.error('Error fetching history notes:', err)
        setError((prev) => ({ ...prev, historyNotes: err.message }))
      } finally {
        setLoading((prev) => ({ ...prev, historyNotes: false }))
      }
    }

    fetchHistoryNotes()
  }, [bookedEventId, patientId, currentPortal, extractedMentorId, patientHistoryGetApiSignature])

  // Fetch vitals
  useEffect(() => {
    if (!bookedEventId || !patientId || !clinicId) return
    if (useBatchedVitals) return

    const fetchVitals = async () => {
      setLoading((prev) => ({ ...prev, vitals: true }))
      try {
        const resp = await getDataWithParams(`/api/secure/patient/vitals`, {
          patientId,
          clinicId,
          bookedEventId,
        })
        if (resp?.status === 200 && isCommonResponseSuccessfulV2(resp)) {
          const vitalsData = resp?.data?.data?.vital
          if (Array.isArray(vitalsData) && vitalsData.length > 0) {
            setVitals(vitalsData[0]) // Get the first vital record for this event
          }
        }
      } catch (err) {
        console.error('Error fetching vitals:', err)
        setError((prev) => ({ ...prev, vitals: err.message }))
      } finally {
        setLoading((prev) => ({ ...prev, vitals: false }))
      }
    }

    fetchVitals()
  }, [bookedEventId, patientId, clinicId, useBatchedVitals])

  useEffect(() => {
    if (!useBatchedVitals) return
    setLoading((prev) => ({ ...prev, vitals: !!batchedVitalsLoading && !batchedVitalsReady }))
    if (batchedVitalsReady) {
      setVitals(batchedVital || null)
    }
  }, [useBatchedVitals, batchedVitalsLoading, batchedVitalsReady, batchedVital, bookedEventId])

  // Fetch private notes
  useEffect(() => {
    if (!bookedEventId || !patientId || hidePrivateNotes) return
    if (Array.isArray(mentorPrivateNotes)) return

    const fetchPrivateNotes = async () => {
      setLoading((prev) => ({ ...prev, privateNotes: true }))
      try {
        // Determine API based on portal - no fallback
        let apiEndpoint
        if (currentPortal === 'clinic-assistant') {
          // Clinic Assistant Portal - use patient API (clinic assistants don't have teacher API)
          apiEndpoint = `/api/secure/patient/private-notes?patientId=${patientId}`
        } else if (currentPortal === 'support') {
          // Support Portal - use teacher API (support typically uses same as mentor)
          apiEndpoint = `/api/secure/teacher/private-notes?patientId=${patientId}`
        } else if (currentPortal === 'mentor') {
          // Mentor Portal - use teacher API
          apiEndpoint = `/api/secure/teacher/private-notes?patientId=${patientId}`
        } else {
          // Default to teacher API if portal cannot be determined (for backward compatibility)
          // Fallback to role-based check if portal detection fails
          apiEndpoint =
            role === 'doctor' || role === 'mentor'
              ? `/api/secure/teacher/private-notes?patientId=${patientId}`
              : `/api/secure/patient/private-notes?patientId=${patientId}`
        }
        const notesData = await getPrivateNotesForEndpoint(apiEndpoint)
        const eventNote = notesData.find((note) => note?.bookedEventId === bookedEventId)
        setPrivateNotes(eventNote)
      } catch (err) {
        console.error('Error fetching private notes:', err)
        setError((prev) => ({ ...prev, privateNotes: err.message }))
      } finally {
        setLoading((prev) => ({ ...prev, privateNotes: false }))
      }
    }

    fetchPrivateNotes()
  }, [bookedEventId, patientId, role, hidePrivateNotes, currentPortal, mentorPrivateNotes])

  // Don't render anything if no data and not loading
  if (
    !loading.historyNotes &&
    !loading.vitals &&
    !loading.privateNotes &&
    !historyNotes &&
    !vitals &&
    !privateNotes
  ) {
    return null
  }

  const renderHistoryNotes = () => {
    if (loading.historyNotes) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            Loading history notes...
          </Typography>
        </Box>
      )
    }
    if (error.historyNotes) {
      return null // Silently fail
    }
    if (historyNotes?.content?.content) {
      return (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            backgroundColor: '#f9f9f9',
          }}
          className="hide-on-print-only"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <History sx={{ fontSize: 18, color: '#1976d2' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1976d2' }}>
              Patient History Notes
            </Typography>
          </Box>
          <Box
            sx={{
              fontSize: '14px',
              color: '#333',
              lineHeight: 1.6,
              '& p': { margin: '4px 0' },
            }}
            dangerouslySetInnerHTML={{ __html: historyNotes.content.content }}
          />
        </Box>
      )
    }
    return null
  }

  const renderVitals = () => {
    if (loading.vitals) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            Loading vitals...
          </Typography>
        </Box>
      )
    }
    if (error.vitals) {
      return null // Silently fail
    }
    if (vitals) {
      const vitalFields = [
        { key: 'bloodPressure', label: 'Blood Pressure' },
        { key: 'heartRate', label: 'Heart Rate' },
        { key: 'temperature', label: 'Temperature' },
        { key: 'weight', label: 'Weight' },
        { key: 'height', label: 'Height' },
        { key: 'bmi', label: 'BMI' },
        { key: 'spo2', label: 'SpO2' },
        { key: 'bloodSugar', label: 'Blood Sugar' },
      ]

      const hasVitalData = vitalFields.some((field) => vitals[field.key] != null)

      if (hasVitalData) {
        return (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              backgroundColor: '#f9f9f9',
            }}
            className="hide-on-print-only"
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <MonitorHeart sx={{ fontSize: 18, color: '#d32f2f' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#d32f2f' }}>
                Vitals
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {vitalFields.map((field) => {
                if (vitals[field.key] != null) {
                  return (
                    <Box key={field.key}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                        {field.label}:
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {vitals[field.key]}
                        {field.key === 'temperature' && '°C'}
                        {field.key === 'weight' && ' kg'}
                        {field.key === 'height' && ' cm'}
                        {field.key === 'heartRate' && ' bpm'}
                        {field.key === 'spo2' && '%'}
                        {field.key === 'bloodSugar' && ' mg/dL'}
                      </Typography>
                    </Box>
                  )
                }
                return null
              })}
            </Box>
          </Box>
        )
      }
    }
    return null
  }

  const renderPrivateNotes = () => {
    if (loading.privateNotes) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="caption" color="text.secondary">
            Loading private notes...
          </Typography>
        </Box>
      )
    }
    if (error.privateNotes) {
      return null // Silently fail
    }
    if (privateNotes?.content) {
      return (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            border: '1px solid #cecece',
            borderRadius: '4px',
            backgroundColor: 'rgba(206,206,206,0.3)',
          }}
          className="hide-on-print-only"
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Notes sx={{ fontSize: 18, color: '#7200a4' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#7200a4' }}>
              Private Note
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#7200a4', whiteSpace: 'pre-wrap' }}>
            {privateNotes.content}
          </Typography>
        </Box>
      )
    }
    return null
  }

  return (
    <Box sx={{ mt: 1 }}>
      {renderHistoryNotes()}
      {renderVitals()}
      {renderPrivateNotes()}
    </Box>
  )
}

